// ─── BUNDEL di STOREFRONT — view-model MURNI, tanpa React/I-O ───────────────
//
// Dipakai halaman `/bundling`, `/bundling/[slug]`, blok "Lebih hemat dalam bundel"
// di halaman produk, dan seksi "Bundel Hemat" di `/promo` & beranda.
//
// Pembagian kerja (jangan dirumuskan ulang di sini):
//   • rumus harga BUNDEL (mode, aturan termurah, alokasi) → `bundle.ts`;
//   • harga TERPISAH tiap komponen → `pricing.ts` lewat jalur yang SAMA dengan
//     `ProductDetail` (`resolveSelectionPrice` → `sellableSelectionPrice`,
//     aturan merek `levelForProduct`) — resolver checkout memakai tangga yang
//     sama, jadi angka di layar = angka yang dikunci saat checkout;
//   • berkas ini hanya merangkai keduanya untuk layar: varian mana yang boleh
//     dipilih per komponen, alasan "tidak tersedia", stok, jumlah maksimum,
//     dan kartu ringkas.
//
// Istilah: BUNDEL & KOMPONEN di kode — tak pernah "paket" (itu paket pengiriman).
// Di LAYAR fiturnya bernama "Bundling" (putusan pemilik 2026-10-22).

import {
  BUNDLE_MIN_COMPONENTS,
  bundleAllowsLevel,
  bundleCustomerSlot,
  bundleLevelFixedPrice,
  bundleNotApplicableText,
  bundleSavingPercent,
  isBundleRunning,
  maxBundleQty,
  staleAllowedOptionIds,
  variantFitsComponent,
  type Bundle,
  type BundleComponent,
  type BundlePriceMode,
  type BundleProductType,
  type BundleQuote
} from "@/lib/bundle";
import { MAX_BUNDLE_QTY } from "@/lib/bundleCart";
import { levelForProduct } from "@/lib/groupBrandDiscount";
import {
  deriveVariantLabel,
  isComboBlocked,
  isSelectionComplete,
  optionSelectionKey,
  pricingSelection,
  shouldUseInheritedPricing,
  type BlockedComboKeys
} from "@/lib/lazyVariants";
import { quotaRemaining } from "@/lib/poQuota";
import {
  formatPrice,
  resolveSelectionPrice,
  sellableSelectionPrice,
  variantPriceExtra
} from "@/lib/pricing";
import { priceExtraNote } from "@/lib/priceExtraLabel";
import {
  isOrderableStatus,
  isStockTracked,
  isVariationEligible,
  type PoQuotaContext,
  type Product,
  type ProductVariation,
  type VariantGroup
} from "@/lib/products";
import type { ProductPromo } from "@/lib/promo";
import type { CustomerGroup } from "@/lib/reseller-groups";

// ─── Periode & daftar ────────────────────────────────────────────────────────

export type BundleRunState = "berjalan" | "nonaktif" | "terjadwal" | "berakhir";

/** Keadaan periode bundel SEKARANG — `isBundleRunning` dengan alasannya. */
export function bundleRunState(
  bundle: Pick<Bundle, "isActive" | "startsAt" | "endsAt">,
  now: Date
): BundleRunState {
  if (!bundle.isActive) return "nonaktif";
  if (isBundleRunning(bundle, now)) return "berjalan";
  const start = bundle.startsAt ? Date.parse(bundle.startsAt) : NaN;
  if (Number.isFinite(start) && now.getTime() < start) return "terjadwal";
  return "berakhir";
}

const tanggal = (iso: string): string => {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  return new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Asia/Jakarta"
  }).format(new Date(t));
};

/** Kalimat pembeli untuk bundel di luar periodenya (`null` = sedang berjalan). */
export function bundleRunStateText(
  state: BundleRunState,
  bundle: Pick<Bundle, "startsAt">
): string | null {
  switch (state) {
    case "berjalan":
      return null;
    case "nonaktif":
      return "Bundling ini sedang tidak aktif.";
    case "terjadwal": {
      const mulai = bundle.startsAt ? tanggal(bundle.startsAt) : "";
      return mulai ? `Bundling ini baru berlaku mulai ${mulai}.` : "Bundling ini belum berlaku.";
    }
    case "berakhir":
    default:
      return "Bundling ini sudah berakhir.";
  }
}

/** Bundel yang sedang berjalan, urut nama — daftar `/bundling`, `/promo`, sitemap. */
export function runningBundles<T extends Pick<Bundle, "isActive" | "startsAt" | "endsAt" | "name">>(
  bundles: readonly T[],
  now: Date
): T[] {
  return bundles
    .filter((b) => isBundleRunning(b, now))
    .sort((a, b) => a.name.localeCompare(b.name, "id"));
}

/**
 * Bundel yang BERHAK bagi pembeli ini (`level_slots`). Tamu, pelanggan umum,
 * dan grup nonaktif dinilai slot 0 — aturan `bundleAllowsLevel` +
 * `bundleCustomerSlot`, cermin kelayakan promo.
 */
export function bundlesForBuyer<T extends Pick<Bundle, "levelSlots">>(
  bundles: readonly T[],
  group: CustomerGroup
): T[] {
  const slot = bundleCustomerSlot(group);
  return bundles.filter((b) => bundleAllowsLevel(b, slot));
}

/**
 * Isi seksi "Bundel Hemat" di BERANDA: bundel yang berhak bagi pembeli ini,
 * tipe TAB AKTIF lebih dulu, dipotong `limit`. Lintas-tipe seperti rail
 * "Sedang Promo" (`promoRailProducts`) — tab Pre-Order tak menyembunyikan
 * bundel Ready Stock, hanya menaruhnya di belakang. Urutan dalam tiap tipe =
 * urutan masukan (urutan server).
 */
export function homeBundleCards<T extends Pick<Bundle, "levelSlots" | "productType">>(
  bundles: readonly T[],
  group: CustomerGroup,
  activeType: BundleProductType,
  limit: number
): T[] {
  if (limit <= 0) return [];
  const active: T[] = [];
  const rest: T[] = [];
  for (const b of bundlesForBuyer(bundles, group)) {
    (b.productType === activeType ? active : rest).push(b);
  }
  return active.concat(rest).slice(0, limit);
}

// ─── Ketersediaan komponen ───────────────────────────────────────────────────

/** Bagian produk yang cukup untuk menilai ketersediaan & menyusun kartu. */
export type BundleProductSummary = Pick<
  Product,
  "id" | "name" | "slug" | "images" | "status" | "productType"
>;

export type ComponentUnavailableReason =
  | "dihapus"
  | "tidak_dijual"
  | "tipe_berbeda"
  | "tidak_bisa_dipesan"
  | "batas_varian_basi";

/** Id opsi HIDUP per produk — bahan alasan `batas_varian_basi`. */
export type LiveOptionIdsOf = ReadonlyMap<string, { has(id: string): boolean }>;

/** Id opsi hidup satu produk lengkap (grup → opsi). */
export function liveOptionIds(groups: readonly VariantGroup[]): Set<string> {
  const out = new Set<string>();
  for (const g of groups) for (const o of g.options) out.add(o.id);
  return out;
}

/**
 * Kenapa komponen ini tak bisa dipesan SEKARANG (`null` = bisa).
 * `product` absen = produknya nonaktif / tak terbaca toko ini (pembaca publik
 * hanya memulangkan produk aktif) — Ready Stock nonaktif sendiri saat stok 0.
 *
 * `liveOptions` = id opsi produk itu yang masih hidup, bila DIKETAHUI. Batas
 * varian yang merujuk opsi terhapus → `batas_varian_basi` (mig 8260, bughunt
 * G8-17): checkout menolak komponen itu, jadi layar menyebutnya lebih dulu.
 * Absen = tak dinilai — kartu ringan (`getCachedCardsByIds`) mengosongkan
 * `variantGroups`, jadi grup kosong di sana BUKAN berarti opsinya terhapus.
 */
export function componentUnavailableReason(
  bundle: Pick<Bundle, "productType">,
  component: Pick<BundleComponent, "productId"> & { allowedOptionIds?: readonly string[] },
  product: Pick<Product, "status" | "productType"> | null | undefined,
  liveOptions?: { has(id: string): boolean } | null
): ComponentUnavailableReason | null {
  if (!component.productId) return "dihapus";
  if (!product) return "tidak_dijual";
  const type = product.productType ?? "preorder";
  if (type !== bundle.productType) return "tipe_berbeda";
  if (!isOrderableStatus(product.status, type)) return "tidak_bisa_dipesan";
  if (liveOptions && staleAllowedOptionIds(component, liveOptions).length) return "batas_varian_basi";
  return null;
}

/** Nama tampilan komponen: nama produk, lalu label, lalu urutan. */
export function componentDisplayName(
  component: Pick<BundleComponent, "label">,
  product: Pick<Product, "name"> | null | undefined,
  index: number
): string {
  const name = product?.name?.trim();
  if (name) return name;
  return component.label?.trim() || `Barang ke-${index + 1}`;
}

/**
 * Judul kecil kotak komponen di halaman bundel: "1. Kakak" bila admin memberi
 * label, "Barang 1" bila tidak — bukan "1. Barang 1" (nomor yang sama dua kali).
 */
export function componentHeading(
  component: Pick<BundleComponent, "label">,
  index: number
): string {
  const label = component.label?.trim();
  return label ? `${index + 1}. ${label}` : `Barang ${index + 1}`;
}

export function componentUnavailableText(
  reason: ComponentUnavailableReason,
  name: string,
  productType: BundleProductType
): string {
  switch (reason) {
    case "dihapus":
      return "Salah satu barang di bundling ini sudah dihapus toko.";
    case "tidak_dijual":
      return `${name} sudah tidak dijual.`;
    case "tipe_berbeda":
      return `${name} tidak lagi cocok dengan bundling ini.`;
    case "batas_varian_basi":
      // Jujur, bukan "sedang diperbarui": komponen macet sampai admin memilih
      // ulang. "Ada … yang" benar juga untuk batas yang hanya sebagian basi.
      return `Ada varian ${name} di bundling ini yang sudah dihapus toko.`;
    case "tidak_bisa_dipesan":
    default:
      return productType === "preorder"
        ? `PO ${name} sudah ditutup.`
        : `${name} sedang tidak bisa dipesan.`;
  }
}

/**
 * Alasan bundel TIDAK TERSEDIA (`null` = bisa dipesan): periode lebih dulu,
 * lalu komponen pertama yang tak bisa dipesan. Ditampilkan apa adanya — bundel
 * tak pernah disembunyikan diam-diam atau menyusut jadi lebih sedikit barang.
 * `liveOptionIdsOf` (opsional): id opsi hidup per produk — produk yang tak
 * ada di peta tak dinilai batas variannya (lihat `componentUnavailableReason`).
 */
export function bundleUnavailableText(
  bundle: Pick<
    Bundle,
    "isActive" | "startsAt" | "endsAt" | "productType" | "components"
  >,
  productsById: ReadonlyMap<string, Pick<Product, "name" | "status" | "productType">>,
  now: Date,
  liveOptionIdsOf?: LiveOptionIdsOf | null
): string | null {
  const runText = bundleRunStateText(bundleRunState(bundle, now), bundle);
  if (runText) return runText;
  const comps = [...bundle.components].sort((a, b) => a.position - b.position);
  if (comps.length < BUNDLE_MIN_COMPONENTS) return "Isi bundling ini belum lengkap.";
  for (const [i, c] of comps.entries()) {
    const product = c.productId ? productsById.get(c.productId) : undefined;
    const live = c.productId ? liveOptionIdsOf?.get(c.productId) : undefined;
    const reason = componentUnavailableReason(bundle, c, product, live);
    if (reason) {
      return componentUnavailableText(reason, componentDisplayName(c, product, i), bundle.productType);
    }
  }
  return null;
}

// ─── Kartu bundel (server → klien) ───────────────────────────────────────────

export type BundleCardComponent = {
  id: string;
  productId: string | null;
  /** Nama produk (atau label/urutan bila produknya tak terbaca). */
  name: string;
  label: string | null;
};

/**
 * Fakta tambahan ukuran per PRODUK komponen yang bertambahan (bughunt D15-25,
 * `fetchBundleExtraFactsServer`): varian aktif BER-HARGA sendiri + petanya opsi
 * → grup (bahan `variantFitsComponent`). Produk tanpa tambahan sama sekali
 * tidak masuk peta (tambahan minimalnya pasti 0).
 */
export type BundleExtraFacts = ReadonlyMap<
  string,
  {
    variants: ReadonlyArray<{ optionIds: readonly string[]; extra: number }>;
    optionGroupOf: ReadonlyMap<string, string>;
  }
>;

/**
 * Σ tambahan TERKECIL yang tak terhindarkan per komponen: varian ber-harga
 * yang lolos batas opsi komponen, tambahan termurahnya. Komponen tanpa fakta
 * (produk tanpa tambahan) atau tanpa varian ber-harga yang lolos (harga dari
 * warisan/produk — tak diketahui di sini) = 0, perilaku lama. `null` = fakta
 * gagal dimuat → 0.
 */
export function bundleCardMinExtras(
  bundle: Pick<Bundle, "components">,
  facts: BundleExtraFacts | null | undefined
): number {
  if (!facts) return 0;
  let total = 0;
  for (const c of bundle.components) {
    const f = c.productId ? facts.get(c.productId) : undefined;
    if (!f) continue;
    let min: number | null = null;
    for (const v of f.variants) {
      if (!variantFitsComponent(c, v.optionIds, f.optionGroupOf)) continue;
      const extra = Number.isFinite(v.extra) && v.extra > 0 ? Math.round(v.extra) : 0;
      min = min === null ? extra : Math.min(min, extra);
      if (min === 0) break;
    }
    total += min ?? 0;
  }
  return total;
}

/**
 * Bentuk RINGAN bundel untuk kartu `/bundling`, `/promo`, dan blok halaman
 * produk — dirakit server, dikirim ke klien. Tanpa harga terpisah: kartu tak
 * memuat varian komponen (halaman bundel yang menghitung harga lengkap).
 */
export type BundleCardData = {
  id: string;
  slug: string;
  name: string;
  productType: BundleProductType;
  priceMode: BundlePriceMode;
  fixedPrice: number | null;
  discountValue: number | null;
  levelPrices: Record<number, number>;
  levelSlots: number[];
  /** Foto sampul, atau kolase foto komponen (maks 4). */
  images: string[];
  components: BundleCardComponent[];
  /** Alasan tak bisa dipesan; `null` = tersedia. */
  unavailable: string | null;
  /**
   * Σ tambahan ukuran terkecil yang tak terhindarkan (`bundleCardMinExtras`,
   * bughunt D15-25) — mode harga tetap memajang harga + ini. Absen = 0.
   */
  minExtras?: number;
};

/**
 * Foto kartu/header: sampul bundel bila ada; kalau tidak, foto pertama tiap
 * produk komponen (urut posisi, tanpa duplikat) — kolase, tanpa perlu admin
 * mengunggah apa pun.
 */
export function bundleCollageImages(
  bundle: Pick<Bundle, "coverImageUrl" | "components">,
  productsById: ReadonlyMap<string, Pick<Product, "images">>,
  max = 4
): string[] {
  if (bundle.coverImageUrl) return [bundle.coverImageUrl];
  const out: string[] = [];
  const seenProducts = new Set<string>();
  for (const c of [...bundle.components].sort((a, b) => a.position - b.position)) {
    if (!c.productId || seenProducts.has(c.productId)) continue;
    seenProducts.add(c.productId);
    const img = productsById.get(c.productId)?.images?.[0];
    if (img && !out.includes(img)) out.push(img);
    if (out.length >= max) break;
  }
  return out;
}

export function buildBundleCardData(
  bundle: Bundle,
  productsById: ReadonlyMap<string, BundleProductSummary>,
  now: Date,
  extraFacts?: BundleExtraFacts | null,
  liveOptionIdsOf?: LiveOptionIdsOf | null
): BundleCardData {
  const comps = [...bundle.components].sort((a, b) => a.position - b.position);
  return {
    id: bundle.id,
    slug: bundle.slug,
    name: bundle.name,
    productType: bundle.productType,
    priceMode: bundle.priceMode,
    fixedPrice: bundle.fixedPrice,
    discountValue: bundle.discountValue,
    levelPrices: bundle.levelPrices,
    levelSlots: bundle.levelSlots,
    images: bundleCollageImages(bundle, productsById),
    components: comps.map((c, i) => {
      const product = c.productId ? productsById.get(c.productId) : undefined;
      return {
        id: c.id,
        productId: c.productId,
        name: componentDisplayName(c, product, i),
        label: c.label
      };
    }),
    unavailable: bundleUnavailableText(bundle, productsById, now, liveOptionIdsOf),
    minExtras: bundleCardMinExtras(bundle, extraFacts)
  };
}

/** Alamat halaman bundel. */
export function bundlePath(slug: string): string {
  return `/bundling/${encodeURIComponent(slug)}`;
}

/** "Gamis Aira + Koko Aira" — ringkasan isi untuk kartu. */
export function bundleComponentsLine(card: Pick<BundleCardData, "components">): string {
  return groupNames(card.components.map((c) => c.name)).join(" + ");
}

/** ["A","B","A"] → ["2× A","B"] — urutan kemunculan pertama dipertahankan. */
function groupNames(names: readonly string[]): string[] {
  const counts = new Map<string, number>();
  for (const n of names) counts.set(n, (counts.get(n) ?? 0) + 1);
  return [...counts.entries()].map(([n, k]) => (k > 1 ? `${k}× ${n}` : n));
}

/**
 * Baris kartu di halaman PRODUK: komponen LAIN selain produk yang sedang
 * dilihat ("+ Koko Aira"). Satu kemunculan produk ini dibuang (komponen yang
 * sedang dilihat); komponen lain dari produk yang SAMA (Family Set Dad/Mom/Girl)
 * disebut lewat labelnya bila ada. `""` = tak ada komponen lain.
 */
export function bundleOtherComponentsLine(
  card: Pick<BundleCardData, "components">,
  productId: string,
  maxNames = 3
): string {
  let skipped = false;
  const others: string[] = [];
  for (const c of card.components) {
    if (!skipped && c.productId === productId) {
      skipped = true;
      continue;
    }
    others.push(c.productId === productId ? c.label?.trim() || c.name : c.name);
  }
  if (!others.length) return "";
  const grouped = groupNames(others);
  const shown = grouped.slice(0, maxNames);
  const rest = grouped.length - shown.length;
  return `+ ${shown.join(" + ")}${rest > 0 ? ` + ${rest} lainnya` : ""}`;
}

/**
 * Petunjuk hemat RINGAN untuk kartu (tanpa memuat varian): mode potongan
 * selalu berarti "dari harga Anda", jadi janjinya benar untuk semua level.
 * Mode harga tetap tak punya petunjuk persen — kartunya memajang harganya.
 */
export function bundleSavingHint(
  card: Pick<BundleCardData, "priceMode" | "discountValue">
): string | null {
  const v = Number(card.discountValue);
  if (card.priceMode === "persen") {
    if (!Number.isFinite(v) || v <= 0 || v >= 100) return null;
    return `Hemat ${new Intl.NumberFormat("id-ID", { maximumFractionDigits: 2 }).format(v)}%`;
  }
  if (card.priceMode === "nominal") {
    if (!Number.isFinite(v) || v <= 0) return null;
    return `Hemat ${formatPrice(v)}`;
  }
  return null;
}

/**
 * Harga kartu untuk mode HARGA TETAP — harga bundel pembeli ini
 * (`bundleLevelFixedPrice`, cermin SQL) + tambahan ukuran yang TAK
 * terhindarkan (`minExtras`: komponen yang batas opsinya hanya menyisakan
 * varian bertambahan — bughunt D15-25; kartu tak pernah memajang harga yang
 * tak bisa dibayar). Mode potongan bergantung varian pilihan → `null` (kartu
 * memakai `bundleSavingHint`).
 */
export function bundleCardPrice(
  card: Pick<BundleCardData, "priceMode" | "fixedPrice" | "levelPrices" | "minExtras">,
  group: CustomerGroup
): number | null {
  if (card.priceMode !== "tetap") return null;
  const base = bundleLevelFixedPrice(card, group);
  if (base === null) return null;
  const extras = card.minExtras;
  return typeof extras === "number" && Number.isFinite(extras) && extras > 0 ? base + Math.round(extras) : base;
}

// ─── Varian per komponen ─────────────────────────────────────────────────────

/** id opsi → id grup opsi, bahan `variantFitsComponent`. */
export function optionGroupMap(groups: readonly VariantGroup[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const g of groups) for (const o of g.options) out.set(o.id, g.id);
  return out;
}

/**
 * Pohon opsi yang BOLEH dipilih untuk komponen ini — semantik SAMA dengan
 * `variantFitsComponent`: grup yang punya opsi terpilih di `allowedOptionIds`
 * dipangkas ke opsi itu; grup tanpa pilihan dibiarkan utuh. Dipakai pemilih
 * varian (kedua mode), jadi pembeli tak pernah melihat opsi di luar bundel.
 *
 * Batas yang merujuk opsi terhapus (mig 8260) → SETIAP grup dikosongkan: tak
 * ada kombinasi yang lolos, cermin `variantFitsComponent`. Dulu grupnya
 * dibiarkan utuh — pemilih melebar ke semua opsi. Komponen itu sendiri sudah
 * "Tidak tersedia" (`batas_varian_basi`), jadi pemilihnya tak ditampilkan.
 */
export function restrictGroupsForComponent(
  groups: VariantGroup[],
  component: Pick<BundleComponent, "allowedOptionIds">
): VariantGroup[] {
  const allowed = new Set(component.allowedOptionIds ?? []);
  if (!allowed.size) return groups;
  if (staleAllowedOptionIds(component, liveOptionIds(groups)).length) {
    return groups.map((g) => ({ ...g, options: [] }));
  }
  return groups.map((g) => {
    const kept = g.options.filter((o) => allowed.has(o.id));
    if (!kept.length || kept.length === g.options.length) return g;
    return { ...g, options: kept };
  });
}

/** Varian produk yang lolos batas opsi komponen (mode biasa). */
export function componentVariations(
  product: Pick<Product, "variations" | "variantGroups">,
  component: Pick<BundleComponent, "allowedOptionIds">
): ProductVariation[] {
  if (!(component.allowedOptionIds ?? []).length) return product.variations;
  const map = optionGroupMap(product.variantGroups);
  return product.variations.filter((v) => variantFitsComponent(component, v.optionIds ?? [], map));
}

/** Pilihan opsi (mode malas) lolos batas opsi komponen? */
export function selectionFitsComponent(
  product: Pick<Product, "variantGroups">,
  component: Pick<BundleComponent, "allowedOptionIds">,
  optionIds: readonly string[]
): boolean {
  return variantFitsComponent(component, optionIds, optionGroupMap(product.variantGroups));
}

// ─── Harga terpisah satu komponen ────────────────────────────────────────────

/** Warisan harga Fase 4 — bentuk `catalog.inherited` milik `resolveSelectionPrice`. */
export type ComponentInheritedPricing = NonNullable<
  Parameters<typeof resolveSelectionPrice>[1]["inherited"]
>;

export type ComponentSeparatePrice = {
  /** Harga satuan termasuk tambahan; `undefined` = harga menyusul. */
  price: number | undefined;
  /** Tambahan yang termasuk di `price`. */
  priceExtra: number;
};

/**
 * Harga TERPISAH satu komponen untuk pembeli ini — CERMIN `ProductDetail`:
 * aturan merek level diselesaikan per produk (`levelForProduct`), katalog
 * harga sama persis (rentang malas, ragu, sumbu harga, warisan), lalu
 * `sellableSelectionPrice` (angka yang boleh masuk kolom uang).
 * `promos` = promo produk ini yang SUDAH tersaring level pembeli.
 *
 * Satu tambahan khusus BUNDEL: harga FALLBACK (varian tanpa harga sendiri &
 * tanpa warisan → perwakilan / harga produk) hanya dipajang bila server akan
 * menerimanya (`bundleFallbackPriceAccepted`, cermin `bundle_variant_price`).
 * Tangga `pricing.ts` sengaja tak diubah: ia menilai keseragaman atas TOTAL,
 * sedangkan checkout bundel menolak dasar/tambahan yang berbeda-beda — tanpa
 * gerbang ini varian itu bisa dipilih lalu gugur `BUNDLE_INVALID:price_unknown`.
 */
export function componentSeparatePrice(input: {
  product: Product;
  variation: ProductVariation | undefined;
  selectedOptionIds: readonly string[];
  group: CustomerGroup;
  promos: readonly ProductPromo[];
  inherited?: ComponentInheritedPricing | null;
}): ComponentSeparatePrice {
  const { product, variation } = input;
  const ids = [...input.selectedOptionIds];
  const inheritedName =
    variation?.name ??
    (product.variantsLazy && isSelectionComplete(product.variantGroups, ids)
      ? deriveVariantLabel(product.variantGroups, ids)
      : undefined);
  const selection = resolveSelectionPrice(
    // Kombinasi mode malas tanpa baris = varian tanpa harga sendiri (D16-13).
    pricingSelection({
      variation,
      variantsLazy: product.variantsLazy,
      groups: product.variantGroups,
      selectedOptionIds: ids
    }),
    {
      variations: product.variations,
      priceNormal: product.price_normal,
      priceRange: product.variantPriceRange,
      priceUncertain: product.variantPriceUncertain,
      priceAxis: product.priceAxis ?? null,
      inherited: input.inherited ?? null,
      inheritedName
    },
    levelForProduct(input.group, product),
    input.promos
  );
  const sellable = sellableSelectionPrice(selection);
  if (sellable.price !== undefined && selection.fromFallback && !bundleFallbackPriceAccepted(product)) {
    return { price: undefined, priceExtra: 0 };
  }
  return { price: sellable.price, priceExtra: sellable.price === undefined ? 0 : sellable.priceExtra };
}

/**
 * Akankah `bundle_variant_price` (mig 8040) memberi harga pada varian TANPA
 * harga sendiri dan tanpa warisan — yaitu harga yang di layar datang dari
 * FALLBACK (`fromFallback`)? Cermin cabang 2–4 fungsi SQL itu:
 *   • produk ber-sumbu harga (atau sumbunya tak diketahui, `null`) → hanya
 *     warisan yang diterima → TIDAK;
 *   • ada varian berharga → hanya bila SEMUANYA sepakat pada SATU `price_normal`
 *     DAN SATU tambahan (TS `variantPriceSpread` cuma menilai totalnya:
 *     100.000+0 vs 90.000+10.000 "seragam" di layar, NULL di server);
 *   • tak satu pun varian berharga → harga produk, tambahan 0 → YA.
 * Syarat SQL ketiga — tambahan varian TERPILIH sendiri sepakat dengan pemberi
 * pinjaman (bughunt D14-2) — sudah ditegakkan `resolveSelectionPrice`
 * (`borrowedExtraMismatch`) sebelum gerbang ini, jadi tak diulang di sini.
 * Mode malas (daftar varian dibuang) hanya memegang rentang TOTAL, tanpa
 * pecahan dasar/tambahan → KONSERVATIF: diterima hanya bila tak ada varian
 * berharga sama sekali, atau rentangnya satu angka yang SAMA dengan harga
 * produk (tak ada tambahan yang bisa tersembunyi di sana). Rentang ragu
 * (`variantPriceUncertain`) = tidak.
 */
export function bundleFallbackPriceAccepted(
  product: Pick<
    Product,
    "variations" | "variantsLazy" | "variantPriceRange" | "variantPriceUncertain" | "price_normal" | "priceAxis"
  >
): boolean {
  if (product.priceAxis === true || product.priceAxis === null) return false;
  const priced = product.variations.filter((v) => v.price_normal !== undefined);
  if (priced.length > 0) {
    const bases = new Set(priced.map((v) => v.price_normal));
    const extras = new Set(priced.map((v) => variantPriceExtra(v)));
    return bases.size === 1 && extras.size === 1;
  }
  if (!product.variantsLazy) return true;
  if (product.variantPriceUncertain === true) return false;
  const range = product.variantPriceRange ?? null;
  if (range === null) return true;
  return range.min === range.max && range.min === product.price_normal;
}

/**
 * Perlukah harga WARISAN (RPC `preorder_inherited_pricing`) untuk pilihan ini?
 * Gerbang yang SAMA dengan `ProductDetail`:
 *   • mode malas — pilihan lengkap & barisnya belum ada / belum berharga;
 *   • mode biasa — baris tanpa harga sendiri pada produk yang sumbu harganya
 *     tidak dinyatakan `false`, dan barisnya punya opsi.
 * `null` = tak perlu. `key` = kunci cache per produk + kombinasi/baris.
 */
export function inheritedPricingRequest(
  product: Pick<Product, "id" | "variantsLazy" | "variantGroups" | "priceAxis">,
  variation: Pick<ProductVariation, "id" | "price_normal" | "optionIds"> | null | undefined,
  selectedOptionIds: readonly string[]
): { key: string; optionIds: string[] } | null {
  if (product.variantsLazy) {
    const ids = [...selectedOptionIds];
    if (!isSelectionComplete(product.variantGroups, ids)) return null;
    if (!shouldUseInheritedPricing(variation)) return null;
    return { key: `${product.id}|${optionSelectionKey(ids)}`, optionIds: ids };
  }
  const ids = variation?.optionIds ?? [];
  if (!variation || !shouldUseInheritedPricing(variation)) return null;
  if (product.priceAxis === false || ids.length === 0) return null;
  return { key: `${product.id}|v:${variation.id}`, optionIds: [...ids] };
}

/**
 * Varian ini PASTI tanpa harga (harga menyusul) bagi bundel? Varian yang
 * masih mungkin berharga lewat warisan TIDAK dihitung pasti — ia tetap bisa
 * dipilih, dan harganya ditanyakan saat dipilih.
 */
export function variantPriceMissing(
  product: Product,
  variation: ProductVariation,
  group: CustomerGroup,
  promos: readonly ProductPromo[]
): boolean {
  const own = componentSeparatePrice({
    product,
    variation,
    selectedOptionIds: variation.optionIds ?? [],
    group,
    promos,
    inherited: null
  });
  if (own.price !== undefined) return false;
  return inheritedPricingRequest(product, variation, variation.optionIds ?? []) === null;
}

/**
 * Varian yang ditawarkan pemilih komponen (mode biasa): lolos batas opsi DAN
 * tidak pasti-tanpa-harga. Stok/kuota habis TIDAK dibuang di sini — pemilih
 * varian sendiri yang mencoretnya (`VariantGroupSelector`, satu aturan dengan
 * halaman produk).
 */
export function selectableComponentVariations(
  product: Product,
  component: Pick<BundleComponent, "allowedOptionIds">,
  group: CustomerGroup,
  promos: readonly ProductPromo[]
): ProductVariation[] {
  return componentVariations(product, component).filter(
    (v) => !variantPriceMissing(product, v, group, promos)
  );
}

/** Pilihan bawaan: varian pertama yang masih bisa dipesan, kalau tidak yang pertama. */
export function defaultComponentVariation(
  variations: readonly ProductVariation[],
  stockTracked: boolean,
  quota?: PoQuotaContext
): ProductVariation | undefined {
  return variations.find((v) => isVariationEligible(stockTracked, v, quota)) ?? variations[0];
}

/**
 * "Ubah pilihan" dari keranjang (`/bundling/<slug>?ganti=<kunci>`): pilihan
 * awal satu komponen dari entri keranjang — HANYA bila pilihan itu masih bisa
 * dipilih di pemilih halaman ini. `null` = pakai pilihan bawaan.
 *   • mode biasa: varian yang sama masih ditawarkan (`variations` = keluaran
 *     `selectableComponentVariations`) dan masih bisa dipesan (stok habis →
 *     bawaan). Kuota PO belum termuat saat ini; pemilih yang menahannya.
 *   • mode malas: opsi tersimpan (`choice.optionIds`, atau opsi varian yang
 *     kebetulan termuat) lengkap SATU per grup di pohon terpangkas komponen
 *     dan bukan kombinasi yang dimatikan admin.
 */
export function editComponentSelection(input: {
  lazy: boolean;
  /** Mode biasa: varian yang ditawarkan pemilih komponen. */
  variations: readonly ProductVariation[];
  stockTracked: boolean;
  /** Mode malas: pohon opsi yang sudah dipangkas ke batas opsi komponen. */
  groups: readonly VariantGroup[];
  /** Mode malas: varian yang kebetulan termuat (cadangan bila opsi tak tersimpan). */
  loadedVariations?: readonly ProductVariation[];
  blocked: BlockedComboKeys;
  choice: { variationId: string; optionIds?: readonly string[] } | undefined;
}): { variationId: string | null; optionIds: string[] } | null {
  const { choice } = input;
  if (!choice?.variationId) return null;
  if (!input.lazy) {
    const v = input.variations.find((x) => x.id === choice.variationId);
    if (!v || !isVariationEligible(input.stockTracked, v)) return null;
    return { variationId: v.id, optionIds: [...(v.optionIds ?? [])] };
  }
  const stored = choice.optionIds?.length
    ? choice.optionIds
    : input.loadedVariations?.find((x) => x.id === choice.variationId)?.optionIds ?? [];
  const groups = [...input.groups];
  if (!stored.length || stored.length !== groups.length) return null;
  const known = new Set(groups.flatMap((g) => g.options.map((o) => o.id)));
  const optionIds = [...stored];
  if (optionIds.some((id) => !known.has(id))) return null;
  if (!isSelectionComplete(groups, optionIds)) return null;
  if (isComboBlocked(input.blocked, optionIds)) return null;
  return { variationId: null, optionIds };
}

// ─── Stok & jumlah ───────────────────────────────────────────────────────────

/**
 * Batas pcs pilihan ini: Ready Stock = stok varian; PO berkuota = sisa jatah;
 * PO biasa (atau kombinasi malas yang belum bermateri) = `null` (tak dibatasi).
 */
export function componentStockLimit(
  product: Pick<Product, "productType" | "poLimited">,
  variation: Pick<ProductVariation, "id" | "stockQty" | "poQuota"> | null | undefined,
  quotaUsed?: Readonly<Record<string, number>>
): number | null {
  if (isStockTracked(product)) return Math.max(0, Math.floor(variation?.stockQty ?? 0));
  if (!variation) return null;
  return quotaRemaining(product.poLimited, variation.poQuota, quotaUsed?.[variation.id]);
}

/** "Stok 4" / "Sisa kuota 9" / null (PO tak berkuota). */
export function componentStockLabel(
  product: Pick<Product, "productType">,
  limit: number | null
): string | null {
  if (isStockTracked(product)) return limit && limit > 0 ? `Stok ${limit}` : "Stok habis";
  if (limit === null) return null;
  return limit > 0 ? `Sisa kuota ${limit}` : "Kuota habis";
}

/**
 * Jumlah bundel maksimum — dua komponen yang memilih varian SAMA butuh 2 pcs
 * per bundel (`maxBundleQty`). `key` = id varian (atau kunci kombinasi malas).
 * Tanpa batas stok → plafon keranjang (`MAX_BUNDLE_QTY`).
 */
export function bundleQtyLimit(
  choices: readonly { key: string; stock: number | null }[]
): number {
  const max = maxBundleQty(choices.map((c) => ({ variantId: c.key, stock: c.stock })));
  return max === null ? MAX_BUNDLE_QTY : Math.min(max, MAX_BUNDLE_QTY);
}

// ─── Masalah pilihan per komponen ────────────────────────────────────────────

export type ComponentIssue =
  | "belum_lengkap"
  | "tidak_cocok"
  | "tidak_dijual"
  | "memuat"
  | "gagal_memuat"
  | "habis"
  | "kuota_habis"
  | "harga_menyusul";

/**
 * Kenapa pilihan komponen ini belum bisa masuk keranjang (`null` = siap).
 * Urutannya disengaja: pilihan dulu (lengkap, cocok, dijual), lalu pemuatan,
 * lalu stok, lalu harga — pembeli diberi tahu hal yang bisa ia perbaiki lebih
 * dulu.
 */
export function componentIssue(input: {
  product: Pick<Product, "productType" | "variantsLazy" | "poLimited" | "variantGroups" | "absentCombosSellable">;
  /** Varian ter-resolusi (mode malas: `null` = kombinasi belum bermateri). */
  variation: ProductVariation | null | undefined;
  selectedOptionIds: readonly string[];
  fits: boolean;
  /** Mode malas: kombinasi dimatikan admin. */
  comboBlocked?: boolean;
  /** Mode malas: status resolusi kombinasi yang SEDANG dipilih. */
  lazyStatus?: "siap" | "memuat" | "gagal";
  /** Harga warisan sedang/akan ditanyakan. */
  inheritedPending: boolean;
  price: number | undefined;
  stock: number | null;
}): ComponentIssue | null {
  const { product } = input;
  if (product.variantsLazy) {
    if (!isSelectionComplete(product.variantGroups, [...input.selectedOptionIds])) return "belum_lengkap";
    if (!input.fits) return "tidak_cocok";
    if (input.comboBlocked) return "tidak_dijual";
    if (input.lazyStatus === "gagal") return "gagal_memuat";
    if (input.lazyStatus !== "siap") return "memuat";
    // PO berkuota menutup kombinasi baru (mig 3970), begitu pula produk
    // TERTUTUP (B1): tanpa baris = pasti ditolak.
    if (input.variation === null && (product.poLimited || product.absentCombosSellable === false)) {
      return "tidak_dijual";
    }
  } else {
    if (!input.variation) return "belum_lengkap";
    if (!input.fits) return "tidak_cocok";
  }
  if (isStockTracked(product)) {
    if ((input.stock ?? 0) <= 0) return "habis";
  } else if (input.stock === 0) {
    return "kuota_habis";
  }
  if (input.inheritedPending) return "memuat";
  if (input.price === undefined) return "harga_menyusul";
  return null;
}

export function componentIssueText(issue: ComponentIssue): string {
  switch (issue) {
    case "belum_lengkap":
      return "Pilih varian untuk barang ini.";
    case "tidak_cocok":
      return "Pilihan ini tidak termasuk bundling — pilih varian lain.";
    case "tidak_dijual":
      return "Variasi ini tidak dijual — pilih kombinasi lain.";
    case "memuat":
      return "Memuat harga pilihan ini…";
    case "gagal_memuat":
      return "Pilihan ini belum bisa dimuat — periksa koneksi, lalu pilih ulang.";
    case "habis":
      return "Stok pilihan ini habis — pilih varian lain.";
    case "kuota_habis":
      return "Kuota PO pilihan ini habis — pilih varian lain.";
    case "harga_menyusul":
    default:
      return "Harga pilihan ini belum tersedia — pilih varian lain.";
  }
}

// ─── Ringkasan harga di layar ────────────────────────────────────────────────

export type BundlePriceView =
  | {
      kind: "bundel";
      /** Yang dibayar per 1 bundel (termasuk tambahan). */
      total: number;
      /** Harga terpisah per 1 bundel — angka coret. */
      separate: number;
      saving: number;
      savingPercent: number;
    }
  | {
      /** Aturan termurah: harga satuan pembeli ini lebih murah → dibayar terpisah. */
      kind: "terpisah";
      total: number;
      note: string;
    }
  | { kind: "kosong"; note: string };

/** Harga yang dipajang dari hasil `quoteBundle` (`null` = pilihan belum siap). */
export function bundlePriceView(quote: BundleQuote | null): BundlePriceView {
  if (!quote) {
    return { kind: "kosong", note: "Lengkapi pilihan setiap barang untuk melihat harga bundling." };
  }
  if (quote.applies) {
    return {
      kind: "bundel",
      total: quote.bundleTotal,
      separate: quote.separateTotal,
      saving: quote.saving,
      savingPercent: bundleSavingPercent(quote.saving, quote.separateBase)
    };
  }
  if (quote.reason === "tidak_lebih_hemat" && quote.separateTotal !== null) {
    return { kind: "terpisah", total: quote.separateTotal, note: bundleNotApplicableText(quote.reason) };
  }
  return { kind: "kosong", note: bundleNotApplicableText(quote.reason) };
}

/** "Hemat Rp 51.000 (11%)" */
export function bundleSavingLabel(saving: number, percent: number): string {
  return percent > 0 ? `Hemat ${formatPrice(saving)} (${percent}%)` : `Hemat ${formatPrice(saving)}`;
}

/**
 * Sub-teks tambahan ukuran di harga bundel — kalimat yang SAMA dengan halaman
 * produk ("Termasuk tambahan ukuran XXL Rp 20.000"). Lebih dari satu komponen
 * bertambahan → satu kalimat jumlahnya tanpa subjek.
 */
export function bundleExtraNote(
  parts: readonly { extra: number; subject: string | null }[]
): string | null {
  const withExtra = parts.filter((p) => p.extra > 0);
  if (!withExtra.length) return null;
  if (withExtra.length === 1) return priceExtraNote(withExtra[0].extra, withExtra[0].subject);
  return priceExtraNote(
    withExtra.reduce((s, p) => s + p.extra, 0),
    null
  );
}
